/**
 * LGPD Block A — beta upload privacy warning.
 *
 * Rendered next to every document upload surface (Documents Library,
 * Reference Library and the guided ingestion modal) so the user always
 * sees the beta data rules before sending a file. Pure presentational
 * component — no state, no API calls, no behavior change to the upload
 * flows that host it.
 *
 * The wording is mandated by the approved LGPD Block A scope: beta
 * environment, prohibited content categories, test/low-risk documents
 * only, AI processing disclosure and user responsibility.
 */

interface Props {
  /**
   * Condensed single-paragraph variant for tight surfaces (the guided
   * ingestion modal). Covers the same five points as the full list.
   */
  compact?: boolean
}

const BETA_POLICY_HREF = '/legal/politica-dados-beta.html'

export function BetaUploadNotice({ compact = false }: Props) {
  if (compact) {
    return (
      <div
        role="note"
        data-testid="beta-upload-notice"
        className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-3.5 py-2.5 text-[12px] leading-relaxed text-amber-100"
      >
        <strong>Ambiente beta:</strong> não envie documentos sensíveis,
        confidenciais, médicos, financeiros, jurídicos, documentos de
        identidade, senhas, chaves de API, dados de menores de idade nem
        documentos de terceiros sem autorização. Use apenas documentos de
        teste, sintéticos, públicos ou de baixo risco — eles podem ser
        processados por IA para gerar respostas, e você é responsável pelo
        que envia.{' '}
        <a
          href={BETA_POLICY_HREF}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-amber-200 underline underline-offset-2 hover:text-amber-50"
        >
          Política de Dados do Beta
        </a>
      </div>
    )
  }

  return (
    <div
      role="note"
      data-testid="beta-upload-notice"
      className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-[12.5px] leading-relaxed text-amber-100"
    >
      <p className="m-0 font-semibold text-amber-200">
        Ambiente beta — atenção ao enviar documentos
      </p>
      <ul className="m-0 mt-1.5 list-disc space-y-1 pl-4">
        <li>
          Não envie documentos sensíveis, confidenciais, médicos,
          financeiros, jurídicos, documentos de identidade (RG, CPF, CNH,
          passaporte), senhas, chaves de API, dados de menores de idade nem
          documentos de terceiros sem autorização.
        </li>
        <li>
          Use apenas documentos de teste, sintéticos, públicos ou de baixo
          risco.
        </li>
        <li>
          Os documentos enviados podem ser processados por inteligência
          artificial (IA) para gerar respostas.
        </li>
        <li>
          Você é responsável por enviar somente documentos que está
          autorizado a utilizar.
        </li>
      </ul>
      <p className="m-0 mt-1.5">
        <a
          href={BETA_POLICY_HREF}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-amber-200 underline underline-offset-2 hover:text-amber-50"
        >
          Leia a Política de Dados do Beta
        </a>
      </p>
    </div>
  )
}
